import React from 'react';
import './ScoreSummary.css';

/**
 * The round's five counts — EAGLE, BIRDIE, PAR, BOGEY, DBL+ — each figure over
 * its word. Used by both pages that draw markers: the leaderboard's status
 * band and the finalise scorecard's landscape title bar.
 *
 * WORDS, NOT ICONS. The counts used to carry 10px shape pips mirroring the
 * markers on the card; at that size they read as noise, and a word is what a
 * golfer reads anyway. The labels are the SHORT forms on purpose — singular,
 * and DBL+ for doubles or worse — because on the leaderboard band three of
 * them share half a 360px phone beside the clock (~42px each), and they are
 * measured to fit there.
 *
 * A bogey and a double are COUNTED APART. The band used to show "Bogey+", one
 * figure for both, while the card above it drew them as two different marks.
 *
 * `counts` is `{ eagle, birdie, par, bogey, double }` — the shape `countResults`
 * (utils/scoreResult.js) returns, which the leaderboard builds from the
 * server's round_stats and the finalise page from its own scores.
 *
 * `inline` lays each count out on one line (figure, then word) for a bar with
 * no height to spare. The default stacks the figure over its word.
 *
 * `centre` puts something in the MIDDLE of the strip — the leaderboard's clock
 * — with the two under-par counts on its left and the three par-or-over on its
 * right. Each side gets exactly half of what is left, so the centre piece is
 * on the strip's centre line whatever the counts are.
 */
export const SUMMARY_ITEMS = [
  ['eagle', 'Eagle', 'eagles'],
  ['birdie', 'Birdie', 'birdies'],
  ['par', 'Par', 'pars'],
  ['bogey', 'Bogey', 'bogeys'],
  ['double', 'Dbl+', 'double bogeys or worse'],
];

const ScoreSummary = ({ counts, inline = false, centre = null, className = '' }) => (
  <ul
    className={`sc-summary${inline ? ' sc-summary--inline' : ''}${centre ? ' sc-summary--split' : ''}${className ? ` ${className}` : ''}`}
    aria-label="Scoring"
  >
    {SUMMARY_ITEMS.flatMap(([kind, label, spoken], i) => {
      const value = counts?.[kind] || 0;
      const item = (
        <li className={`sc-summary-item sc-summary-item--${kind}`} key={kind}>
          <span className="sc-summary-sr">{`${value} ${spoken}`}</span>
          <span className="sc-summary-count" aria-hidden="true">{value}</span>
          <span className="sc-summary-label" aria-hidden="true">{label}</span>
        </li>
      );
      // The centre piece goes between the under-par pair and the rest.
      return centre && i === 2
        ? [<li className="sc-summary-centre" role="presentation" key="centre">{centre}</li>, item]
        : [item];
    })}
  </ul>
);

export default ScoreSummary;
